'use client';
import React, { useEffect, useState } from 'react';
import { useHeader } from '@/components/complex/header';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import { useRouter } from 'next/navigation';
import api from '@/api';
import { InquiryTicketDto } from '@/libs/api/inquiry';
import Typography from '@/components/primitives/typography';
import { ChevronRight, Plus } from 'lucide-react';
import { HDivider } from '@/components/primitives/divider';
import styles from './styles.module.scss';
import Button from '@/components/primitives/button';
import { useAuth } from '@/context/auth-context';

const InquiryListScreen = () => {
  const router = useRouter();
  const auth = useAuth();
  const [tickets, setTickets] = useState<InquiryTicketDto[]>([]);
  const [loading, setLoading] = useState(true);

  useHeader({
    useBack: true,
    title: '문의하기',
  });

  useBottomNavigation(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await api.inquiry.getMyTickets();
        setTickets(response.list);
      } catch (error) {
        console.error('Failed to fetch tickets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'OPEN':
        return '접수됨';
      case 'IN_PROGRESS':
        return '처리 중';
      case 'CLOSED':
        return '답변 완료';
      default:
        return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'OPEN':
        return styles.statusOpen;
      case 'IN_PROGRESS':
        return styles.statusInProgress;
      case 'CLOSED':
        return styles.statusClosed;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      {!auth.me ?
        <div className={styles.emptyContainer}>
          <Typography weight="extra-thin">문의하시려면 로그인해주세요</Typography>
        </div> :
        <></>
      }

      <div className={styles.listContainer}>
        {!!auth.me ? loading ? (
          <div className={styles.emptyContainer}>
            <Typography>로딩 중...</Typography>
          </div>
        ) : tickets.length === 0 ? (
          <div className={styles.emptyContainer}>
            <Typography weight="extra-thin">문의 내역이 없어요</Typography>
          </div>
        ) : (
          tickets.map((ticket) => (
            <div key={ticket.id}>
              <div
                className={styles.ticketItem}
                onClick={() => router.push(`/inquiry/${ticket.id}`)}
              >
                <div className={styles.ticketInfo}>
                  <div className={styles.ticketTop}>
                    <span className={`${styles.statusBadge} ${getStatusClass(ticket.status)}`}>
                      {getStatusText(ticket.status)}
                    </span>
                    <Typography className={styles.ticketDate}>
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </div>
                  <Typography className={styles.ticketTitle}>{ticket.title}</Typography>
                </div>
                <ChevronRight className={styles.chevronIcon} size={20} />
              </div>
              <HDivider />
            </div>
          ))
        ) : <></>}
      </div>

      <div className={styles.headerAction}>
        <Button
          variant="primary"
          onClick={() => router.push('/inquiry/create')}
          className={styles.createButton}
        >
          {!auth.me ? <>로그인하고 문의하기</> : <>
            <Plus size={18} />
            문의하기
          </>}
        </Button>
      </div>
    </div>
  );
};

export default InquiryListScreen;
